/**
 * ActivityFeed Component
 * Displays activity log for workspace or user
 */

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography, Alert, Skeleton, Stack, alpha, Button, CircularProgress } from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import { AppDispatch, RootState } from '../../store';
import {
  fetchWorkspaceActivityFeed,
  fetchBoardActivityFeed,
  fetchRecentActivity,
  fetchEntityActivity,
} from '../../store/slices/activitySlice';
import { ActivityLog, ActivityType, EntityType } from '../../api/types/ActivityTypes';
import { formatDistanceToNow } from 'date-fns/formatDistanceToNow';

interface ActivityFeedProps {
  workspaceId?: string;
  boardId?: string;
  entityType?: EntityType;
  entityId?: string;
  limit?: number;
  showRecent?: boolean; // Show only recent (last 24 hours)
}

const ActivitySkeletonRow: React.FC = () => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 1.5,
      p: 1.5,
      borderRadius: 2,
      border: '1px solid',
      borderColor: 'divider',
    }}
  >
    <Skeleton variant="circular" width={36} height={36} />
    <Box sx={{ flex: 1 }}>
      <Skeleton variant="text" width="70%" height={20} />
      <Skeleton variant="text" width="30%" height={16} />
    </Box>
  </Box>
);

export const ActivityFeed: React.FC<ActivityFeedProps> = ({
  workspaceId,
  boardId,
  entityType,
  entityId,
  limit = 50,
  showRecent = false,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [loadingMore, setLoadingMore] = useState(false);

  const isEntityMode = !!entityType && !!entityId;
  const entityKey = isEntityMode ? `${entityType}:${entityId}` : '';

  const activities = useSelector((state: RootState) => {
    if (isEntityMode) {
      return state.activity.entityActivities[entityKey] || [];
    }
    if (boardId) {
      return state.activity.boardActivities[boardId] || [];
    }
    if (showRecent && workspaceId) {
      return state.activity.recentActivities[workspaceId] || [];
    }
    if (workspaceId) {
      return state.activity.workspaceActivities[workspaceId] || [];
    }
    return [];
  });

  const total = useSelector((state: RootState) => {
    if (boardId) {
      return state.activity.boardActivityTotal[boardId] ?? 0;
    }
    if (workspaceId) {
      return state.activity.workspaceActivityTotal[workspaceId] ?? 0;
    }
    return 0;
  });

  const loading = useSelector((state: RootState) =>
    isEntityMode
      ? state.activity.entityActivityLoading
      : showRecent
      ? state.activity.recentActivityLoading
      : state.activity.workspaceActivityLoading,
  );

  const error = useSelector((state: RootState) =>
    isEntityMode
      ? state.activity.entityActivityError
      : showRecent
      ? state.activity.recentActivityError
      : state.activity.workspaceActivityError,
  );

  useEffect(() => {
    if (isEntityMode) {
      dispatch(fetchEntityActivity({ entityType: entityType!, entityId: entityId!, limit }));
    } else if (boardId) {
      dispatch(fetchBoardActivityFeed({ boardId, params: { limit } }));
    } else if (showRecent && workspaceId) {
      dispatch(fetchRecentActivity({ workspaceId, hours: 24 }));
    } else if (workspaceId) {
      dispatch(fetchWorkspaceActivityFeed({ workspaceId, params: { limit } }));
    }
  }, [dispatch, workspaceId, boardId, isEntityMode, entityType, entityId, limit, showRecent]);

  const canLoadMore =
    !isEntityMode && !showRecent && (!!boardId || !!workspaceId) && activities.length < total;

  const handleLoadMore = async () => {
    setLoadingMore(true);
    try {
      if (boardId) {
        await dispatch(
          fetchBoardActivityFeed({ boardId, params: { limit, offset: activities.length } }),
        );
      } else if (workspaceId) {
        await dispatch(
          fetchWorkspaceActivityFeed({
            workspaceId,
            params: { limit, offset: activities.length },
          }),
        );
      }
    } finally {
      setLoadingMore(false);
    }
  };

  const getActivityIcon = (activityType: ActivityType): string => {
    switch (activityType) {
      case ActivityType.WORKSPACE_CREATED:
      case ActivityType.BOARD_CREATED:
      case ActivityType.FOLDER_CREATED:
      case ActivityType.TASK_CREATED:
        return '➕';
      case ActivityType.WORKSPACE_UPDATED:
      case ActivityType.BOARD_UPDATED:
      case ActivityType.FOLDER_UPDATED:
      case ActivityType.TASK_UPDATED:
        return '✏️';
      case ActivityType.WORKSPACE_DELETED:
      case ActivityType.BOARD_DELETED:
      case ActivityType.FOLDER_DELETED:
      case ActivityType.TASK_DELETED:
        return '🗑️';
      case ActivityType.TASK_COMPLETED:
        return '✅';
      case ActivityType.TASK_REOPENED:
        return '🔄';
      case ActivityType.MEMBER_ADDED:
        return '👤';
      case ActivityType.MEMBER_REMOVED:
        return '👋';
      case ActivityType.TASK_ASSIGNED:
        return '📌';
      case ActivityType.TASK_UNASSIGNED:
        return '📍';
      case ActivityType.TASK_STATUS_CHANGED:
        return '🔀';
      case ActivityType.TASK_PRIORITY_CHANGED:
        return '🔥';
      case ActivityType.TASK_DUE_DATE_CHANGED:
        return '📅';
      case ActivityType.SPRINT_STARTED:
        return '🚀';
      case ActivityType.SPRINT_COMPLETED:
        return '🏆';
      case ActivityType.TASK_LINKED_TO_EPIC:
        return '🔗';
      case ActivityType.COMMENT_ADDED:
        return '💬';
      case ActivityType.ATTACHMENT_ADDED:
      case ActivityType.ATTACHMENT_DELETED:
        return '📎';
      default:
        return '📝';
    }
  };

  const getActivityDescription = (activity: ActivityLog): string => {
    if (activity.description) {
      return activity.description;
    }

    const userName = activity.user?.name || 'Someone';
    const entityName = activity.entityName || 'item';

    switch (activity.activityType) {
      case ActivityType.TASK_CREATED:
        return `${userName} created task "${entityName}"`;
      case ActivityType.TASK_COMPLETED:
        return `${userName} completed task "${entityName}"`;
      case ActivityType.TASK_UPDATED:
        return `${userName} updated task "${entityName}"`;
      case ActivityType.TASK_STATUS_CHANGED:
        return `${userName} changed status of "${entityName}"`;
      case ActivityType.TASK_REOPENED:
        return `${userName} reopened task "${entityName}"`;
      case ActivityType.TASK_PRIORITY_CHANGED:
        return `${userName} changed priority of "${entityName}"`;
      case ActivityType.TASK_DUE_DATE_CHANGED:
        return `${userName} changed due date of "${entityName}"`;
      case ActivityType.TASK_ASSIGNED:
        return `${userName} assigned task "${entityName}"`;
      case ActivityType.TASK_UNASSIGNED:
        return `${userName} unassigned task "${entityName}"`;
      case ActivityType.COMMENT_ADDED:
        return `${userName} commented on "${entityName}"`;
      case ActivityType.ATTACHMENT_ADDED:
        return `${userName} added an attachment to "${entityName}"`;
      case ActivityType.ATTACHMENT_DELETED:
        return `${userName} removed an attachment from "${entityName}"`;
      case ActivityType.BOARD_CREATED:
        return `${userName} created board "${entityName}"`;
      case ActivityType.MEMBER_ADDED:
        return `${userName} added a member to the workspace`;
      case ActivityType.WORKSPACE_CREATED:
        return `${userName} created workspace "${entityName}"`;
      case ActivityType.SPRINT_STARTED:
        return `${userName} started sprint "${entityName}"`;
      case ActivityType.SPRINT_COMPLETED:
        return `${userName} completed sprint "${entityName}"`;
      case ActivityType.TASK_LINKED_TO_EPIC:
        return `${userName} linked task "${entityName}" to epic`;
      default:
        return `${userName} performed an action`;
    }
  };

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
        {isEntityMode ? 'Activity' : showRecent ? 'Recent Activity (24 hours)' : 'Activity Feed'}
      </Typography>

      {loading ? (
        <Stack spacing={1.25}>
          {Array.from({ length: 5 }).map((_, i) => (
            <ActivitySkeletonRow key={i} />
          ))}
        </Stack>
      ) : error ? (
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          Error loading activity feed: {error}
        </Alert>
      ) : !activities || activities.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 5,
            px: 2,
            borderRadius: 2,
            border: '1px dashed',
            borderColor: 'divider',
          }}
        >
          <HistoryIcon sx={{ fontSize: 40, color: 'action.disabled', mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            No activity to display yet
          </Typography>
        </Box>
      ) : (
        <Stack spacing={1.25}>
          {activities.map((activity) => (
            <Box
              key={activity.id}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
                p: 1.5,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                transition: 'border-color 0.15s ease, background-color 0.15s ease',
                '&:hover': {
                  borderColor: alpha('#0073ea', 0.3),
                  bgcolor: alpha('#0073ea', 0.03),
                },
              }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  flexShrink: 0,
                  borderRadius: '50%',
                  bgcolor: alpha('#0073ea', 0.08),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.1rem',
                }}
              >
                {getActivityIcon(activity.activityType)}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ color: 'text.primary' }}>
                  {getActivityDescription(activity)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatDistanceToNow(new Date(activity.createdAt), {
                    addSuffix: true,
                  })}
                </Typography>
              </Box>
            </Box>
          ))}
        </Stack>
      )}

      {canLoadMore && (
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={handleLoadMore}
            disabled={loadingMore}
            startIcon={loadingMore ? <CircularProgress size={14} /> : undefined}
          >
            {loadingMore ? 'Loading…' : `Load more (${activities.length} of ${total})`}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default ActivityFeed;
